<template>
    <main-layout>
        <div class="page-head" slot="header">
            <div><div class="page-kicker">Tenancies</div><h2>Associations</h2></div>
            <div>
                <el-button size="small" type="primary" @click="syncFromBuildium">Sync from Buildium</el-button>
            </div>
        </div>

        <!-- 搜索栏 -->
        <section class="page-section">
            <div class="form-inline">
                <div class="form-item">
                    <div class="form-item-label">Association Name</div>
                    <div class="form-item-input">
                        <el-input size="medium" class="w400" :clearable="true" v-model="params.name"/>
                    </div>
                </div>
                <div class="form-item">
                    <el-button size="medium" type="primary" @click="onSearch">{{ $t('post.search') }}</el-button>
                </div>
            </div>
        </section>

        <!-- 表格展示 -->
        <section class="page-section">
            <el-table :data="dataList" v-loading="loading" element-loading-text="Loading..." @selection-change="onSelectionChange" border>
                <el-table-column width="45" type="selection" />
                <el-table-column prop="Id" label="ID" width="80"/>
                <el-table-column prop="Name" label="Association" />
                <el-table-column prop="location" label="Location"/>
                <el-table-column prop="Status" label="Status" width="80">
                    <template slot-scope="scope">
                        <el-tag :type="scope.row.IsActive ? 'success' : 'danger'">
                            {{ scope.row.IsActive ? 'Active' : 'Inactive' }}
                        </el-tag>
                    </template>
                </el-table-column>

                <!-- Type 只能在编辑模式下修改 -->
                <el-table-column prop="Type" label="Type" width="150">
                    <template slot-scope="scope">
                        <div v-if="scope.row.isEditing">
                            <el-select v-model="scope.row.Type" placeholder="Select Type" @change="onTypeChange(scope.row)">
                                <el-option label="Commercial" value="Commercial"></el-option>
                                <el-option label="Residential" value="Residential"></el-option>
                            </el-select>
                        </div>
                        <div v-else>
                            {{ scope.row.Type }}
                        </div>
                    </template>
                </el-table-column>

                <!-- GST Account 只能在编辑模式下修改 -->
                <el-table-column prop="GSTAccount" label="GST Account" width="200">
                    <template slot-scope="scope">
                        <div v-if="scope.row.isEditing">
                            <el-select v-model="scope.row.GSTAccount" placeholder="Select GST Account"
                                       :disabled="scope.row.Type !== 'Commercial'">
                                <el-option label="(Empty)" value=""></el-option>
                                <el-option label="GST Paid Out" value="GST Paid Out"></el-option>
                                <el-option label="GST_Remittance to CRA" value="GST_Remittance to CRA"></el-option>
                            </el-select>
                        </div>
                        <div v-else>
                            {{ scope.row.GSTAccount }}
                        </div>
                    </template>
                </el-table-column>

                <!-- 操作列 -->
                <el-table-column label="Actions" width="200">
                    <template slot-scope="scope">
                        <div v-if="!scope.row.isEditing">
                            <el-button size="mini" type="primary" @click="enableEditing(scope.row)">Edit</el-button>
                        </div>
                        <div v-else>
                            <el-button size="mini" type="success" @click="saveChanges(scope.row)">Save</el-button>
                            <el-button size="mini" @click="cancelEditing(scope.row)">Cancel</el-button>
                        </div>
                    </template>
                </el-table-column>
            </el-table>

            <div class="table-edit-footer">
                <div class="table-actions">
                    <el-button size="small" type="primary" :disabled="!selectionIds.length" @click="onBatchDelete">
                        {{ $t('common.batch_delete') }}
                    </el-button>
                </div>
                <el-pagination background layout="prev, pager, next, total"
                               :total="total" :page-size="pageSize"
                               :current-page="page" @current-change="onPageChange"/>
            </div>
        </section>
    </main-layout>
</template>

<script>
import ApiService from "../utils/ApiService";
import Pagination from "../mixins/Pagination";

export default {
    name: "Associations",
    mixins: [Pagination],
    data() {
        return {
            autoSearch: true,
            params: {
                name: ''
            },
            originalData: {} // 存储原始数据
        }
    },
    methods: {
        listApi() {
            return '/buildium/associations';
        },
        listParams() {
            return this.params;
        },
        syncFromBuildium() {
            const loading = this.$loading({
                lock: true,
                text: 'Synchronizing...',
                spinner: 'el-icon-loading',
                background: 'rgba(0, 0, 0, 0.7)'
            });
            this.$api.get('/buildium/associations/sync').then(response => {
                this.$message.success('Sync completed');
                this.fetchList();
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                loading.close();
            });
        },
        onSearch() {
            this.fetchList();
        },
        onBatchDelete() {
            let ids = this.selectionIds.map(item => item.Id);
            this.$confirm(this.$t('common.delete_tips'), this.$t('common.delete_confirm'), {
                type: 'warning'
            }).then(() => {
                ApiService.delete('/buildium/associations/batch', { data: { ids } }).then(() => {
                    this.$message.success('Deleted successfully');
                    this.fetchList();
                }).catch(reason => {
                    this.$message.error(reason.message);
                });
            }).catch(() => {
                this.$message.info('Canceled');
            });
        },
        // 当 Type 变更时自动清空 GSTAccount
        onTypeChange(row) {
            if (row.Type === 'Residential') {
                row.GSTAccount = "";
            }
        },
        // 开启编辑模式
        enableEditing(row) {
            this.$set(row, 'isEditing', true);
            this.originalData[row.Id] = { Type: row.Type, GSTAccount: row.GSTAccount };
        },
        // 取消编辑
        cancelEditing(row) {
            if (this.originalData[row.Id]) {
                row.Type = this.originalData[row.Id].Type;
                row.GSTAccount = this.originalData[row.Id].GSTAccount;
            }
            this.$set(row, 'isEditing', false);
        },
        // 保存修改
        saveChanges(row) {
            ApiService.put(`/buildium/associations/${row.Id}`, { Type: row.Type, GSTAccount: row.GSTAccount })
                .then(() => {
                    this.$message.success('Updated successfully');
                    this.$set(row, 'isEditing', false);
                })
                .catch(reason => {
                    this.$message.error(reason.message || 'Update failed');
                });
        }
    },
    mounted() {
        this.fetchList();
    }
}
</script>

<style scoped>
</style>
